import { forwardRef, memo } from 'react';
import size from 'lodash/size';
// Material UI imports
import Box from '@mui/material/Box';
// Skillmore UI Components
import FilterSelector from '@empathco/ui-components/src/elements/FilterSelector';
import LevelSelector from '@empathco/ui-components/src/elements/LevelSelector';
// local imports
import { Manager } from '../graphql/types';
import { LookupItem } from '../models/lookupItem';
import { SkillLevel } from '../models/skill';
import ChainOfCommandSelector from '../v3/ChainOfCommandSelector';

type EmployeesWithSkillFiltersProps = {
  readonly withHierarchy?: boolean | null;
  readonly withLevel?: boolean | null;
  readonly withLevelSearch?: boolean | null;
  readonly uid?: string | null;
  readonly me?: string | null;
  readonly leader?: string | null;
  readonly countries?: LookupItem[] | null;
  readonly country?: number;
  readonly states?: LookupItem[] | null;
  readonly state?: number;
  readonly managers?: Manager[] | null;
  readonly manager?: string;
  readonly level?: SkillLevel;
  readonly setCountry: (id: number) => void;
  readonly setState: (id: number) => void;
  readonly setManager: (id: string, hasTeams?: boolean) => void;
  readonly setLevel: (level: SkillLevel) => void;
  readonly disabled?: boolean | null;
  readonly disabledState?: boolean | null;
}

const EmployeesWithSkillFilters = forwardRef<HTMLDivElement, EmployeesWithSkillFiltersProps>(({
  withHierarchy = false,
  withLevel = false,
  withLevelSearch = false,
  uid,
  me,
  leader,
  countries,
  country = 0,
  setCountry,
  states,
  state = 0,
  setState,
  managers,
  manager = '0',
  setManager,
  level,
  setLevel,
  disabled = false,
  disabledState = false
}, ref) => (
  <Box ref={ref} display="flex" gap={2} flexWrap="wrap">
    <Box flex="1 0 auto">
      <FilterSelector
          type="country"
          choices={countries}
          value={country}
          onChange={setCountry}
          disabled={disabled}
      />
    </Box>
    <Box flex="1 0 auto">
      <FilterSelector
          type="state"
          choices={disabledState ? undefined : states}
          value={disabledState ? 0 : state}
          onChange={setState}
          disabled={disabled || disabledState}
      />
    </Box>
    {withLevel ? (
      <Box flex="1 0 auto">
        <LevelSelector
            search={withLevelSearch ? true : undefined}
            value={level}
            onChange={setLevel as (level: number) => void}
            disabled={disabled ? true : undefined}
        />
      </Box>
    ) : undefined}
    {!managers || size(managers) >= 1 ? (
      <Box flex="1 0 auto" display="flex" justifyContent="flex-end">
        <ChainOfCommandSelector
            uid={uid}
            me={withHierarchy ? me : undefined}
            leader={withHierarchy ? leader : undefined}
            expandable={withHierarchy}
            managers={managers}
            value={manager}
            onChange={setManager}
            disabled={disabled}
        />
      </Box>
    ) : undefined}
  </Box>
));

EmployeesWithSkillFilters.displayName = 'EmployeesWithSkillFilters';

export default memo(EmployeesWithSkillFilters);
